// ===========================================================================
// The chat this tab was in, kept across a reload.
//
// The server holds a 1:1 open for RECONNECT_GRACE_MS after a socket dies and
// hands it back to the same person on their next HELLO (server matchmaker.js:
// the new socket adopts the held member). A page RELOAD threw that away on
// the client alone -- pull-to-refresh, a phone's browser discarding the tab
// and reloading it on return, a crash -- because the page came back to the
// landing with no memory of the chat, and the hold ran out while the person
// sat there (Poli, 2026-09-26, "keeps dropping out"). So while a 1:1 is up
// the tab keeps a seed here, and on load app.js walks straight back in while
// the seed is younger than the hold.
//
// sessionStorage on purpose: one tab, gone when the tab is closed for good,
// so a chat is never resumed in a tab it was not in. Every access is wrapped
// (private windows, embedded browsers). No config import, so it runs under
// node --test. ASCII only.
// ===========================================================================

const KEY = 'lumi.chat';

/**
 * A 1:1 is up in this tab: remember what it takes to start again. Written at
 * the match and again whenever the tab is hidden, so the seed's clock is the
 * last moment the chat was known to be alive.
 * @param {{ birthYear: number, mode: string }} chat
 * @returns {boolean} whether it was stored
 */
export function rememberChat({ birthYear, mode } = {}, nowMs = Date.now()) {
  if (!Number.isFinite(birthYear) || typeof mode !== 'string' || !mode) return false;
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ birthYear, mode, at: nowMs }));
    return true;
  } catch {
    return false;
  }
}

/** The chat ended, or was left: nothing to walk back into. */
export function forgetChat() {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* nothing to clear, or nowhere to clear it from */
  }
}

/**
 * The chat to walk back into, or null: none, malformed, or older than the
 * server's hold. Consumed on the way out, whatever the answer, so a start
 * that fails is an ordinary landing and never a loop.
 * @param {number} graceMs the server's hold (RECONNECT_GRACE_MS)
 * @returns {{ birthYear: number, mode: string }|null}
 */
export function chatToResume(graceMs, nowMs = Date.now()) {
  let raw = null;
  try {
    raw = JSON.parse(sessionStorage.getItem(KEY) || 'null');
  } catch {
    raw = null;
  }
  forgetChat();
  if (!raw || typeof raw !== 'object') return null;
  if (!Number.isFinite(raw.birthYear) || typeof raw.mode !== 'string' || !raw.mode || !Number.isFinite(raw.at)) return null;
  if (nowMs < raw.at || nowMs - raw.at > graceMs) return null;
  return { birthYear: raw.birthYear, mode: raw.mode };
}

export default { rememberChat, forgetChat, chatToResume };
